import padStart from 'lodash/padStart';
import replace from 'lodash/replace';
import round from 'lodash/round';
import toInteger from 'lodash/toInteger';
import trim from 'lodash/trim';

import { CURRENCIES } from '@savgroup-front-common/constants/shared';
import {
  ReverseMoney,
  ReverseMoneyNumberAmount,
} from '@savgroup-front-common/types';

import { intl } from '../helpers/intl';
import {
  isNegativePrice,
  isPrice,
  isSafeFractionalNumber,
  isSafePrice,
} from '../helpers/validators';

import { getCurrencySymbol } from './currency';

const NON_BREAKING_SPACES = /[\u00A0\u202F\u2009]/g;

interface FormatMoneyForDisplayOptions {
  amount?: number | string;
  currency?: CURRENCIES;
  shouldDisplayCurrency?: boolean;
  shouldStripIfInteger?: boolean;
}

export const formatMoneyForDisplay = ({
  amount,
  currency = CURRENCIES.EUR,
  shouldDisplayCurrency = true,
  shouldStripIfInteger = false,
}: FormatMoneyForDisplayOptions): string => {
  const numericAmount =
    typeof amount === 'string' ? Number(replace(amount, ',', '.')) : amount;

  if (numericAmount === undefined || !Number.isFinite(numericAmount)) {
    return '';
  }

  const fractionDigits =
    shouldStripIfInteger && Number.isInteger(numericAmount) ? 0 : 2;

  const formattedNumber = (
    intl?.formatNumber(numericAmount, {
      style: 'decimal',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }) ?? numericAmount.toFixed(fractionDigits)
  ).replace(NON_BREAKING_SPACES, ' ');

  if (!shouldDisplayCurrency) {
    return formattedNumber;
  }

  return `${formattedNumber} ${getCurrencySymbol(currency)}`;
};

export const formatStripeAmount = (
  amount: number,
  currency: CURRENCIES = CURRENCIES.EUR,
  isFractional = false,
): string | undefined => {
  if (!Number.isFinite(amount) && isFractional) {
    return '0';
  }
  if (!Number.isFinite(amount)) {
    return '-';
  }

  const roundedAmount = Math.round(amount);
  const integralPart = (roundedAmount / 100) | 0;
  const fractionalPart = Math.abs((roundedAmount % 100) | 0);

  const isNegativeFractional = roundedAmount < 0 && integralPart === 0;
  const integralPartString = isNegativeFractional ? '-0' : String(integralPart);

  const formattedAmount = `${integralPartString}.${padStart(
    String(fractionalPart),
    2,
    '0',
  )}`;

  if (isFractional) {
    return formattedAmount;
  }

  return intl?.formatNumber(Number(formattedAmount), {
    style: 'currency',
    currency,
  });
};

export const formatPercentageLocale = (
  value: string | number,
): string | undefined => {
  const numericValue =
    typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);

  if (!isFinite(numericValue)) {
    return '';
  }

  return intl?.formatNumber(numericValue / 100, {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatStripeReverseMoneyNumberAmount = (price: {
  amount: number;
  currency: CURRENCIES;
}): string | undefined => {
  const { amount, currency } = price;

  return formatMoneyForDisplay({ amount, currency });
};

export const formatAmountWithCurrency = (
  amount: number,
  currency = CURRENCIES.EUR,
): string => {
  if (!Number.isFinite(amount)) {
    return '-';
  }

  return formatMoneyForDisplay({ amount, currency });
};

export const formatStripeAmountWithoutCurrency = ({
  amount,
  currency = CURRENCIES.EUR,
  isFractional = false,
}: {
  amount: number;
  currency?: CURRENCIES;
  isFractional?: boolean;
}): string => {
  const formattedAmount = formatStripeAmount(amount, currency, isFractional);
  const formattedCurrency = getCurrencySymbol(currency);

  if (formattedAmount === undefined) {
    return '0';
  }

  const replaceFormattedAmount = replace(
    formattedAmount.toString(),
    `${formattedCurrency}`,
    '',
  );

  return trim(replaceFormattedAmount);
};

export const cleanIntlNumber = ({ amount }: { amount: string }): string => {
  // eslint-disable-next-line no-irregular-whitespace
  const removeWhiteSpaceReg = new RegExp(/[ ]/gi);
  const reg = new RegExp('([0-9,.]*)([,.][0-9]{1,2})');
  const formattedAmount = replace(amount, removeWhiteSpaceReg, '');

  const matches = formattedAmount.match(reg);

  if (!matches) {
    return amount;
  }

  const number = matches[1].replace(',', '').replace('.', '');
  const decimal = matches[2];

  return `${number}${decimal}`;
};

export const formatStringToIntlPrice = ({
  amount,
  currency = CURRENCIES.EUR,
}: {
  amount: string;
  currency?: CURRENCIES;
}): string => {
  if (amount === '' || !isSafePrice(amount)) {
    return amount;
  }

  const floatAmount = round(Number(replace(amount, ',', '.')), 2).toFixed(2);

  if (floatAmount === '0.00') {
    return '0';
  }

  const formattedStripeValue = formatStripeAmountWithoutCurrency({
    amount: toInteger(Math.round(Number(floatAmount) * 100)),
    currency,
  });

  return cleanIntlNumber({ amount: formattedStripeValue });
};

export const formatStringToNegativeIntlPrice = ({
  amount,
  currency = CURRENCIES.EUR,
}: {
  amount: string;
  currency?: CURRENCIES;
}): string => {
  if (amount === '' || !(isNegativePrice(amount) || isPrice(amount))) {
    return amount;
  }

  const floatAmount = round(
    Math.abs(Number(replace(amount, ',', '.'))),
    2,
  ).toFixed(2);

  if (floatAmount === '0.00') {
    return '0';
  }

  const formattedStripeValue = formatStripeAmountWithoutCurrency({
    amount: toInteger(Math.round(Number(floatAmount) * 100)),
    currency,
  });

  return `-${cleanIntlNumber({ amount: formattedStripeValue })}`;
};

export const formatReverseMoneyToString = (
  reverseMoney?: ReverseMoney | ReverseMoneyNumberAmount,
  options?: {
    shouldStripIfInteger: boolean;
  },
): string => {
  if (!reverseMoney) {
    return '';
  }

  const { amount, currency } = reverseMoney;

  if (Number.isInteger(amount) && options?.shouldStripIfInteger) {
    return formatMoneyForDisplay({ amount, currency, shouldStripIfInteger: true });
  }

  if (!isSafeFractionalNumber(amount)) {
    return `${amount} ${getCurrencySymbol(currency)}`;
  }

  return formatMoneyForDisplay({ amount, currency });
};
